import { ProjectTreeNodeT } from "../../project-layer-tree/useProjectLayerTree.types";

export interface ManageSelectionState {
  folderIds: string[];
  orgFolderIds: string[];
  systemFolderIds: string[];
  shapefileIds: string[];
  projectShapefileIds: string[];
  allCheckedKeys: string[]; // For Ant Design tree component
}

const TreeNodeHelpers = {
  /**
   * Determines if a node is a deletable layer/shapefile
   */
  isLayerNode: (node: ProjectTreeNodeT): boolean => {
    return node.type === "layer" || node.type === "shapefile";
  },

  isFolderNode: (node: ProjectTreeNodeT): boolean => {
    return node.type === "folder" || node.type === "node";
  },

  processLayerNode: (
    node: ProjectTreeNodeT,
    shapefileIds: string[],
    projectShapefileIds: string[]
  ): void => {
    if (node.projectShapefileId) {
      projectShapefileIds.push(node.projectShapefileId);
    } else if (node.shapefileId) {
      shapefileIds.push(node.shapefileId);
    }
  },

  processFolderNode: (
    node: ProjectTreeNodeT,
    folderIds: string[],
    orgFolderIds: string[],
    systemFolderIds: string[]
  ): void => {
    if (node.shapefileNodeId) {
      const folderSource = node.source || node.folder?.source;
      if (folderSource === "org") {
        orgFolderIds.push(node.shapefileNodeId);
      } else if (folderSource === "mcdan") {
        systemFolderIds.push(node.shapefileNodeId);
      } else {
        // Regular folder (project-owned)
        folderIds.push(node.shapefileNodeId);
      }
    }
  }
};

/**
 * Determines the proper selection state for manage mode, separating folders and shapefiles
 * based on cascading selection rules:
 *
 * - If ALL children of a folder are selected → include only the folder ID
 * - If SOME children of a folder are selected → include individual shapefile IDs
 *
 * This logic ensures the API receives the minimal, non-redundant set of IDs needed
 * for deletion while maintaining proper parent-child relationships.
 */
export const getManageSelectionState = (
  checkedKeys: string[],
  tree: ProjectTreeNodeT[]
): ManageSelectionState => {
  const checkedKeysSet = new Set(checkedKeys);
  const folderIds: string[] = [];
  const orgFolderIds: string[] = [];
  const systemFolderIds: string[] = [];
  const shapefileIds: string[] = [];
  const projectShapefileIds: string[] = [];
  const allCheckedKeys: string[] = [...checkedKeys];

  // Track actual IDs to remove if parent folder covers them
  const toRemoveFromFolders = new Set<string>();
  const toRemoveFromOrgFolders = new Set<string>();
  const toRemoveFromSystemFolders = new Set<string>();
  const toRemoveFromShapefiles = new Set<string>();
  const toRemoveFromProjectShapefiles = new Set<string>();

  /**
   * Single recursive pass processing from bottom up
   * Returns true if this node and ALL its descendants are selected
   */
  const processNode = (node: ProjectTreeNodeT): boolean => {
    const isNodeSelected = checkedKeysSet.has(node.key);

    if (!node.children?.length) {
      // Leaf node (shapefile or empty folder)
      if (isNodeSelected) {
        if (TreeNodeHelpers.isLayerNode(node)) {
          TreeNodeHelpers.processLayerNode(node, shapefileIds, projectShapefileIds);
        } else if (TreeNodeHelpers.isFolderNode(node)) {
          TreeNodeHelpers.processFolderNode(
            node,
            folderIds,
            orgFolderIds,
            systemFolderIds
          );
        }
      }
      return isNodeSelected;
    }

    // Parent node with children - process all children first
    const childResults = node.children.map((child) => ({
      node: child,
      fullySelected: processNode(child)
    }));

    // Check if ALL children are fully selected
    const hasAllChildrenSelected = childResults.every((result) => result.fullySelected);

    if (isNodeSelected && hasAllChildrenSelected && TreeNodeHelpers.isFolderNode(node)) {
      // This folder covers all its children - add folder and mark children for removal
      TreeNodeHelpers.processFolderNode(node, folderIds, orgFolderIds, systemFolderIds);

      // Mark all descendants for removal since parent folder covers them
      const markDescendantsForRemoval = (nodeToMark: ProjectTreeNodeT) => {
        if (TreeNodeHelpers.isFolderNode(nodeToMark) && nodeToMark.shapefileNodeId) {
          const folderSource = nodeToMark.source || nodeToMark.folder?.source;
          if (folderSource === "org") {
            toRemoveFromOrgFolders.add(nodeToMark.shapefileNodeId);
          } else if (folderSource === "mcdan") {
            toRemoveFromSystemFolders.add(nodeToMark.shapefileNodeId);
          } else {
            toRemoveFromFolders.add(nodeToMark.shapefileNodeId);
          }
        } else if (TreeNodeHelpers.isLayerNode(nodeToMark)) {
          if (nodeToMark.projectShapefileId) {
            toRemoveFromProjectShapefiles.add(nodeToMark.projectShapefileId);
          } else if (nodeToMark.shapefileId) {
            toRemoveFromShapefiles.add(nodeToMark.shapefileId);
          }
        }

        nodeToMark.children?.forEach(markDescendantsForRemoval);
      };

      node.children.forEach(markDescendantsForRemoval);

      // Add all descendant keys to allCheckedKeys for UI consistency
      const addDescendantKeys = (nodeToAdd: ProjectTreeNodeT) => {
        if (!allCheckedKeys.includes(nodeToAdd.key)) {
          allCheckedKeys.push(nodeToAdd.key);
        }
        nodeToAdd.children?.forEach(addDescendantKeys);
      };

      node.children.forEach(addDescendantKeys);

      return true; // This entire subtree is fully selected
    }

    // Either node not selected or not all children selected
    return false;
  };

  // Process all root nodes
  tree.forEach(processNode);

  // Remove items that are covered by parent folders
  const finalFolderIds = folderIds.filter((id) => !toRemoveFromFolders.has(id));
  const finalOrgFolderIds = orgFolderIds.filter((id) => !toRemoveFromOrgFolders.has(id));
  const finalSystemFolderIds = systemFolderIds.filter(
    (id) => !toRemoveFromSystemFolders.has(id)
  );
  const finalShapefileIds = shapefileIds.filter((id) => !toRemoveFromShapefiles.has(id));
  const finalProjectShapefileIds = projectShapefileIds.filter(
    (id) => !toRemoveFromProjectShapefiles.has(id)
  );

  return {
    folderIds: Array.from(new Set(finalFolderIds)),
    orgFolderIds: Array.from(new Set(finalOrgFolderIds)),
    systemFolderIds: Array.from(new Set(finalSystemFolderIds)),
    shapefileIds: Array.from(new Set(finalShapefileIds)),
    projectShapefileIds: Array.from(new Set(finalProjectShapefileIds)),
    allCheckedKeys: Array.from(new Set(allCheckedKeys))
  };
};
